import { useState, useRef, useEffect } from "react";
import { bylineText } from "@/features/auth/impersonation/authorByline";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/others/Badges";
import useCurrentUser from "@/features/users/api/useCurrentUser";
import axiosInstance from "@/lib/axios";
import {
  resolveDownloadFilename,
  triggerBlobDownload,
} from "@/utils/downloadDocumentsZip";
import { getFileTypeColor } from "@/utils/convetors";
import { formatDate } from "@/utils/date";
import {
  ChevronDown,
  Download,
  Eye,
  FileText,
  FolderInput,
  Loader2,
  Pencil,
  Trash2,
} from "lucide-react";
import {
  attachmentLabelDisplay,
  normalizeDocAttachmentLabel,
  type AttachmentLabel,
} from "@/utils/attachmentLabels";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/library/dropdown-menu";

export type FolderMoveOption = { id: number | null; label: string };

/** Lead-or-deal re-filing target. `id: null` is the lead itself. */
export type DealMoveOption = { id: number | null; label: string };

interface DocumentItemProps {
  doc: any;
  onEdit?: (doc: any) => void;
  onDelete?: (doc: any) => void;
  onRename?: (id: string, newName: string) => void;
  isRequired?: boolean;
  moveOptions?: FolderMoveOption[];
  onMoveToFolder?: (docId: string, folderId: number | null) => void;
  onAttachmentLabelChange?: (docId: string, label: AttachmentLabel) => void;
  /**
   * The lead + its deals, for re-filing a document whose lead-or-deal answer
   * was wrong. Omitted (or a single entry) = no picker; the grain is still
   * shown as a chip when the document sits on a deal.
   */
  dealOptions?: DealMoveOption[];
  onMoveToDeal?: (docId: string, childId: number | null) => void;
}

export default function DocumentItem({
  doc,
  onEdit,
  onDelete,
  onRename,
  isRequired = false,
  moveOptions,
  onMoveToFolder,
  onAttachmentLabelChange,
  dealOptions,
  onMoveToDeal,
}: DocumentItemProps) {
  const { user } = useCurrentUser(doc.uploadedId);
  // "Switch user": the REAL uploader when a manager uploaded this while
  // viewing as `uploadedId`. Ships on the document row, so no extra lookup.
  // Display only — `uploadedId` still owns the file.
  const switchedByUser = doc.switchedByUser ?? null;
  const [isEditingName, setIsEditingName] = useState(false);
  const [editedName, setEditedName] = useState(doc.name || "");
  const [isDownloading, setIsDownloading] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  // Helper: Handle missing fileType by extracting from name
  const fileType = doc?.type || doc?.fileName?.split(".").pop() || "FILE";
  const label = normalizeDocAttachmentLabel(doc?.attachmentLabel);

  // Deal grain: null = a lead document (the person's own paperwork).
  const dealId = doc?.childId != null ? Number(doc.childId) : null;
  const dealName =
    dealId != null
      ? (dealOptions?.find((o) => o.id === dealId)?.label ?? `Deal #${dealId}`)
      : null;
  // The picker only earns its place when there is somewhere else to file to.
  const canRefile = !!onMoveToDeal && (dealOptions?.length ?? 0) > 1;

  useEffect(() => {
    if (isEditingName && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isEditingName]);

  const handleSaveName = () => {
    if (editedName.trim() !== doc.name && onRename) {
      onRename(doc.id, editedName.trim());
    }
    setIsEditingName(false);
  };

  const handleCancelName = () => {
    setEditedName(doc.name || "");
    setIsEditingName(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      handleSaveName();
    } else if (e.key === "Escape") {
      handleCancelName();
    }
  };

  // Stream through our server endpoint so Content-Disposition preserves the
  // original filename + extension. Linking <a download> straight at the S3
  // URL is silently ignored cross-origin, so the browser saves the file
  // with the S3 key (a random hash, no extension).
  const handleDownload = async () => {
    if (isDownloading || doc?.id == null) return;
    setIsDownloading(true);
    try {
      const res = await axiosInstance.get(`/core/doc/${doc.id}/download`, {
        responseType: "blob",
      });
      triggerBlobDownload(res.data as Blob, resolveDownloadFilename(doc));
    } catch (err: any) {
      toast.error(err?.message || "Failed to download file");
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <div
      key={doc.id}
      className="group flex min-h-[72px] items-center gap-3 rounded-[12px] bg-white/60 px-3 py-3 transition-colors hover:bg-white/80"
    >
      {/* 1. File Icon Section */}
      <div className="relative size-9 shrink-0 flex items-center justify-center">
        <FileText className="size-full text-[#cacfd8]" strokeWidth={1.2} />

        <Badge variant="fileIcon" className={`${getFileTypeColor(fileType)}`}>
          {fileType}
        </Badge>
      </div>

      {/* 2. Document Info (Aligned Properly) */}
      <div className="flex-1 min-w-0 flex flex-col justify-center gap-1">
        {/* Name */}
        {isEditingName && !isRequired ? (
          <div className="flex items-center gap-1 h-6">
            <input
              ref={inputRef}
              type="text"
              value={editedName}
              onChange={(e) => setEditedName(e.target.value)}
              onKeyDown={handleKeyDown}
              onBlur={handleSaveName}
              className="h-full text-[14px] font-medium text-gray-900 border-b border-blue-500 focus:outline-none bg-transparent w-full"
            />
          </div>
        ) : isRequired ? (
          <Tooltip>
            <TooltipTrigger asChild>
              <div className="flex items-center gap-1.5">
                <p className="text-[14px] font-medium text-gray-900 truncate">
                  {doc.name || "Add File Name"}
                </p>
                <span className="text-[10px] font-medium bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded-full leading-none shrink-0">
                  Required
                </span>
              </div>
            </TooltipTrigger>
            <TooltipContent>
              This is a required document. The name cannot be changed.
            </TooltipContent>
          </Tooltip>
        ) : (
          <p
            className="text-[14px] font-medium text-gray-900 truncate cursor-pointer hover:text-blue-600 transition-colors"
            title={doc.name || "Add File Name"}
            onDoubleClick={() => setIsEditingName(true)}
          >
            {doc.name || "Add File Name"}
          </p>
        )}

        <div className="flex items-center gap-2 flex-wrap">
          {onAttachmentLabelChange ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  type="button"
                  className="inline-flex items-center gap-1 rounded-full border border-[#e8eaf4] bg-[#fafbff] pl-2 pr-1 py-0.5 max-w-full hover:bg-[#f3f5ff] transition-colors"
                >
                  <Badge
                    variant={label === "PAYMENT_RECEIPT" ? "choices" : "gray"}
                    className="!text-[10px] !px-2 !py-0 !rounded-none !rounded-l-full border-0"
                  >
                    {attachmentLabelDisplay(doc.attachmentLabel)}
                  </Badge>
                  <ChevronDown className="size-3.5 text-gray-500 shrink-0" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" className="w-48">
                <DropdownMenuLabel className="text-xs font-normal text-muted-foreground">
                  Document type
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                {(
                  [
                    { v: "WORKFILE" as const, t: "Work file" },
                    { v: "PAYMENT_RECEIPT" as const, t: "Payment receipt" },
                  ] as const
                ).map(({ v, t }) => (
                  <DropdownMenuItem
                    key={v}
                    disabled={label === v}
                    onClick={() => onAttachmentLabelChange(String(doc.id), v)}
                  >
                    {t}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <Badge
              variant={label === "PAYMENT_RECEIPT" ? "choices" : "gray"}
              className="!text-[10px] font-medium"
            >
              {attachmentLabelDisplay(doc.attachmentLabel)}
            </Badge>
          )}

          {/* Lead-or-deal grain. A lead document shows nothing when there is
              no picker — "no chip" already reads as "belongs to the person",
              and a chip on every row would drown the deal ones. */}
          {canRefile ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  type="button"
                  className="inline-flex max-w-full items-center gap-1 rounded-full border border-[#e8eaf4] bg-[#fafbff] py-0.5 pl-2 pr-1 transition-colors hover:bg-[#f3f5ff]"
                >
                  <span className="truncate text-[10px] font-medium text-[var(--color-black-8)]">
                    {dealName ?? "Lead document"}
                  </span>
                  <ChevronDown className="size-3.5 shrink-0 text-gray-500" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" className="w-56">
                <DropdownMenuLabel className="text-xs font-normal text-muted-foreground">
                  Belongs to
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                {dealOptions?.map((opt) => (
                  <DropdownMenuItem
                    key={String(opt.id ?? "lead")}
                    disabled={opt.id === dealId}
                    onClick={() => onMoveToDeal?.(String(doc.id), opt.id)}
                  >
                    {opt.label}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            dealName && (
              <Badge variant="indigo" className="!text-[10px] font-medium">
                {dealName}
              </Badge>
            )
          )}
        </div>

        {/* Filename */}
        <p
          className={`${doc.name ? "text-[12px] text-gray-500" : "text-[12px] text-gray-500"} truncate`}
          title={doc.fileName}
        >
          {doc.fileName.split("/").pop()}
        </p>

        {/* Metadata Row: Date • User */}
        <div className="flex items-center gap-2 text-[12px] text-gray-500">
          <span className="shrink-0">{formatDate(doc.createdAt)}</span>
          {doc.updatedAt && doc.updatedAt !== doc.createdAt && (
            <>
              <span className="size-0.5 rounded-full bg-gray-400" />{" "}
              <span className="shrink-0">
                Updated: {formatDate(doc.updatedAt)}
              </span>
            </>
          )}

          {(user?.name || switchedByUser?.name) && (
            <>
              <span className="size-0.5 rounded-full bg-gray-400" />{" "}
              {/* Dot Separator */}
              <span
                className="truncate max-w-[160px]"
                title={
                  switchedByUser?.name
                    ? `${switchedByUser.name} uploaded this while viewing as ${user?.name ?? "another user"}`
                    : undefined
                }
              >
                {bylineText(user?.name, switchedByUser)}
              </span>
            </>
          )}
        </div>
      </div>

      {/* 3. Action Buttons */}
      <div className="flex items-center gap-0.5">
        {/* View */}
        <a href={doc.fileUrl} target="_blank" rel="noreferrer">
          <Button
            variant="ghost"
            size="icon"
            className="size-8 text-gray-500 hover:text-blue-600"
          >
            <Eye className="size-4" strokeWidth={2} />
          </Button>
        </a>

        {/* Download */}
        <Button
          variant="ghost"
          size="icon"
          className="size-8 text-gray-500 hover:text-blue-600"
          onClick={handleDownload}
          disabled={isDownloading}
          title="Download"
        >
          {isDownloading ? (
            <Loader2 className="size-4 animate-spin" strokeWidth={2} />
          ) : (
            <Download className="size-4" strokeWidth={2} />
          )}
        </Button>

        {/* Edit */}
        <Button
          variant="ghost"
          size="icon"
          className="size-8 text-gray-500 hover:text-orange-600"
          onClick={() => onEdit?.(doc)}
        >
          <Pencil className="size-4" strokeWidth={2} />
        </Button>

        {moveOptions && moveOptions.length > 0 && onMoveToFolder && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className="size-8 text-gray-500 hover:text-[#5a5cff]"
                title="Move to folder"
              >
                <FolderInput className="size-4" strokeWidth={2} />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent
              align="end"
              className="max-h-64 w-56 overflow-y-auto"
            >
              <DropdownMenuLabel className="text-xs font-normal text-muted-foreground">
                Move to…
              </DropdownMenuLabel>
              <DropdownMenuSeparator />
              {moveOptions.map((opt) => (
                <DropdownMenuItem
                  key={opt.id === null ? "root" : opt.id}
                  disabled={
                    (doc.folderId ?? null) === (opt.id ?? null)
                  }
                  onClick={() => onMoveToFolder(String(doc.id), opt.id)}
                >
                  <span className="truncate">{opt.label}</span>
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        )}

        {/* Delete */}
        <Button
          variant="ghost"
          size="icon"
          className="size-8 text-red-500 hover:text-red-600"
          onClick={() => onDelete?.(doc)}
        >
          <Trash2 className="size-4" strokeWidth={2} />
        </Button>
      </div>
    </div>
  );
}
